<template>
  <div
    class="multiselect-container"
    :class="listDisplayInlineButtons.length ? ' inline-container' : ''"
  >
    <multiselect
      :id="id"
      :disabled="isDisabled"
      :class="
        listDisplayInlineButtons.length
          ? multiSelectClass + ' item-selection'
          : multiSelectClass
      "
      @open="onMultiselectOpen"
      @close="onMultiselectClose"
      v-model="inputVal"
      :options="optionList"
      :multiple="isMultiple"
      :customLabel="customLabel"
      :searchable="true"
      :close-on-select="closeOnSelect"
      :show-labels="false"
      :placeholder="
        placeholder || getLabelByKeys('PLACE_HOLDERS.TYPE_TO_FILTER')
      "
      :max-height="maxHeight"
      :track-by="optionModel"
      :label="optionLabel"
      @input="handleOnInput($event)"
      @select="handleSelect($event)"
      @remove="handleRemove($event)"
    >
      <template v-slot:option="{ option }">
        <slot name="option" :option="option"></slot>
      </template>
    </multiselect>
    <div class="" v-if="listDisplayInlineButtons.length">
      <button
        v-for="button of listDisplayInlineButtons"
        :key="'button-' + button.id"
        @click="handleClickButton(button)"
        :class="button.class ? button.class : 'btn btn-secondary'"
        style="width: 100px"
      >
        {{ getLabelByKeys(button.keyLangLabel) }}
      </button>
    </div>
    <div
      v-if="
        additionalSelectedInfoFunc &&
        inputVal &&
        additionalSelectedInfoFunc(inputVal)
      "
      class="mb-2"
    >
      <div v-html="additionalSelectedInfoFunc(inputVal)"></div>
    </div>
    <div>
      <small
        v-if="hasSelectAllBtn && isMultiple"
        class="select-all-button"
        @click="selectAll()"
      >
        Select All
      </small>
      <small
        v-if="hasClearButton"
        class="clear-button"
        @click="clearSelection()"
      >
        Clear
      </small>
    </div>

    <div
      class="button-group"
      v-if="listDisplayButtons && listDisplayButtons.length"
    >
      <button
        v-for="button of listDisplayButtons"
        :key="'button-' + button.id"
        @click="handleClickButton(button)"
        :class="button.class ? button.class : 'btn btn-secondary mr-1'"
      >
        {{ button.keyLabelParams? getLabelByKeys(button.keyLangLabel, ...button.keyLabelParams): getLabelByKeys(button.keyLangLabel) }}
      </button>
    </div>
  </div>
</template>
<script>
/*
  This component is used for select value of field' property,
  for example: number of item per row: 1, 2, 3, 4
*/

export default {
  name: "MultiSelect",
  props: {
    id: String,
    isDisabled: {
      type: Boolean,
      default: false,
    },
    value: [String, Number, Array, Object],
    /* List of options, can be list of String or list of Object */
    optionList: {
      type: Array,
      default: () => [],
    },
    multiSelectClass: {
      type: String,
      default: "mb-2 w-100",
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    customLabel: {},
    /* In case optionList is list of Object, optionLabel specifies key of option which is showed in label */
    optionLabel: String,
    /* In case optionList is list of Object, optionLabel specifies key of option for v-model */
    optionModel: String,
    placeholder: {
      type: String,
      default: "",
    },
    hasClearButton: {
      type: Boolean,
      default: false,
    },
    hasSelectAllBtn: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: Number,
      default: 150,
    },
    buttonGroups: Array, // List of buttons attached by the end of multiselect
    additionalSelectedInfoFunc: Function, // Function to display additional information of the selected item
  },
  data() {
    return {
      hasUserAction: false,
    };
  },
  computed: {
    listDisplayButtons() {
      if (!this.buttonGroups || !this.buttonGroups.length) {
        return null;
      }
      let result = this.buttonGroups.filter(
        (button) =>
          button.id !== "add-option" &&
          button.id !== "agency-setting" &&
          !button.id.includes("inline")
      );
      if (this.inputVal) {
        return result;
      }
      return result.filter((button) => !button.showIfSelect);
    },
    listDisplayInlineButtons() {
      if (!this.buttonGroups || !this.buttonGroups.length) {
        return [];
      }
      let result = this.buttonGroups.filter((button) =>
        button.id.includes("inline")
      );
      if (!result.length) {
        return [];
      }
      return result;
    },
    inputVal: {
      get() {
        if (this.optionModel) {
          if (this.value == null) {
            return this.value;
          }
          if (Array.isArray(this.value)) {
            // Multi Selection
            return this.optionList.filter((option) =>
              this.value.includes(option[this.optionModel])
            );
          } else {
            // Single selection
            const selectedOption = this.optionList.find(
              (option) => option[this.optionModel] === this.value
            );
            if (selectedOption) {
              return selectedOption;
            } else {
              return this.value;
            }
          }
        } else {
          return this.value;
        }
      },
      set(val) {
        let result = val;
        if (this.optionModel) {
          if (val) {
            if (Array.isArray(val)) {
              // Multi selection
              result = val.map((item) => item[this.optionModel]);
            } else {
              result = val[this.optionModel];
              // Single selection
            }
          }
        }
        this.$emit("input", result);
        if (this.hasUserAction) {
          this.$emit("userInput", result);
        }
        this.hasUserAction = false;
      },
    },
  },
  methods: {
    handleSelect(info) {
      this.$emit("select", info);
      this.hasUserAction = true;
    },
    handleRemove(info) {
      this.$emit("remove", info);
      this.hasUserAction = true;
    },
    handleOnInput(info) {
      if (info === null) {
        if (!this.isMultiple) {
          this.$emit("input", "");
        }
        return;
      }
      if (this.optionModel && info) {
        this.$emit("change", info[this.optionModel]);
      } else {
        this.$emit("change", info);
      }
    },
    clearSelection() {
      let val;
      if (this.isMultiple) {
        val = [];
      } else {
        val = "";
      }
      this.$emit("input", val);
      this.$emit("userInput", val);
    },
    selectAll() {
      let val;
      if (this.optionModel) {
        val = this.optionList.map((option) => option[this.optionModel]);
      } else {
        val = [...this.optionList];
      }
      this.$emit("input", val);
      this.$emit("userInput", val);
    },
    handleClickButton(button) {
      this.$emit("clickBtn", button.id);
    },
  },
};
</script>
<style scoped>
.multiselect-container {
  width: 100%;
}
.inline-container {
  display: flex;
}
.inline-container > .item-selection {
  flex-grow: 1;
  padding-right: 0.5em;
}
.inline-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 7px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
}
.clear-button {
  margin-top: -0.3rem;
}

.select-all-button {
  float: right;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/common/complex-input-elements/MultiSelect.vue